import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { 
  Building2, 
  Lightbulb, 
  Code2, 
  Rocket, 
  Users, 
  Mail,
  ArrowLeft,
  Bot,
  ShoppingCart,
  Zap,
  Globe,
  CheckCircle2
} from 'lucide-react';

const VortexAIAdvisory = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
      {/* Header */}
      <header className="border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="bg-gradient-to-r from-indigo-500 to-purple-500 p-2 rounded-lg">
                <Building2 className="h-6 w-6 text-white" />
              </div>
              <h1 className="text-2xl font-bold text-white">Vortex AI Advisory</h1>
            </div>
            <Link to="/">
              <Button variant="ghost" className="text-gray-300 hover:text-white hover:bg-white/10">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Speakify
              </Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
        <div className="text-center">
          <div className="inline-flex items-center px-4 py-2 bg-white/10 rounded-full text-purple-300 text-sm font-medium mb-6 backdrop-blur-sm">
            <Bot className="h-4 w-4 mr-2" />
            AI Solutions for E-Commerce
          </div>
          
          <h2 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
            Building the Future of
            <span className="block text-transparent bg-clip-text bg-gradient-to-r from-indigo-400 to-purple-400">
              E-Commerce AI
            </span>
          </h2>
          
          <p className="text-lg md:text-xl text-gray-300 mb-10 max-w-3xl mx-auto leading-relaxed">
            Vortex AI Advisory specializes in creating innovative AI-powered applications 
            for Shopify and e-commerce platforms. We transform complex AI technologies 
            into practical business solutions.
          </p>
        </div>
      </section>

      {/* What We Do Section */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h3 className="text-3xl md:text-4xl font-bold text-white mb-4">What We Do</h3>
            <p className="text-lg text-gray-400 max-w-2xl mx-auto">
              We build intelligent applications that help merchants grow their businesses
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            <Card className="bg-white/5 border-white/10 backdrop-blur-sm hover:bg-white/10 transition-colors">
              <CardContent className="p-6">
                <div className="bg-gradient-to-r from-indigo-500 to-purple-500 w-14 h-14 rounded-xl flex items-center justify-center mb-4">
                  <ShoppingCart className="h-7 w-7 text-white" />
                </div>
                <h4 className="text-xl font-semibold text-white mb-2">Shopify Apps</h4>
                <p className="text-gray-400">
                  Custom Shopify applications that leverage AI to enhance the merchant and customer experience.
                </p>
              </CardContent>
            </Card>
            
            <Card className="bg-white/5 border-white/10 backdrop-blur-sm hover:bg-white/10 transition-colors">
              <CardContent className="p-6">
                <div className="bg-gradient-to-r from-green-500 to-emerald-500 w-14 h-14 rounded-xl flex items-center justify-center mb-4">
                  <Bot className="h-7 w-7 text-white" />
                </div>
                <h4 className="text-xl font-semibold text-white mb-2">AI Integration</h4>
                <p className="text-gray-400">
                  Seamlessly integrate cutting-edge AI technologies like GPT, Claude, and ElevenLabs into your business.
                </p>
              </CardContent>
            </Card>
            
            <Card className="bg-white/5 border-white/10 backdrop-blur-sm hover:bg-white/10 transition-colors">
              <CardContent className="p-6">
                <div className="bg-gradient-to-r from-orange-500 to-red-500 w-14 h-14 rounded-xl flex items-center justify-center mb-4">
                  <Lightbulb className="h-7 w-7 text-white" />
                </div>
                <h4 className="text-xl font-semibold text-white mb-2">Custom Solutions</h4>
                <p className="text-gray-400">
                  Bespoke AI solutions tailored to your unique business challenges and opportunities.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Our Apps Section */}
      <section className="py-16 bg-black/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h3 className="text-3xl md:text-4xl font-bold text-white mb-4">Our Products</h3>
            <p className="text-lg text-gray-400 max-w-2xl mx-auto">
              AI-powered applications built for the Shopify ecosystem
            </p>
          </div>
          
          <div className="max-w-2xl mx-auto">
            <Card className="bg-gradient-to-r from-indigo-500/20 to-purple-500/20 border-indigo-500/30 backdrop-blur-sm">
              <CardContent className="p-8">
                <div className="flex items-start space-x-4">
                  <div className="bg-indigo-600 p-3 rounded-xl">
                    <Zap className="h-8 w-8 text-white" />
                  </div>
                  <div className="flex-1">
                    <h4 className="text-2xl font-bold text-white mb-2">Speakify</h4>
                    <p className="text-gray-300 mb-4">
                      Transform your Shopify products with AI-powered audio descriptions. 
                      Features premium text-to-speech, custom voice cloning, and seamless theme integration.
                    </p>
                    <ul className="space-y-2 mb-6">
                      <li className="flex items-center text-gray-300">
                        <CheckCircle2 className="h-4 w-4 text-green-400 mr-2" />
                        ElevenLabs AI Voices
                      </li>
                      <li className="flex items-center text-gray-300">
                        <CheckCircle2 className="h-4 w-4 text-green-400 mr-2" />
                        Custom Voice Cloning
                      </li>
                      <li className="flex items-center text-gray-300">
                        <CheckCircle2 className="h-4 w-4 text-green-400 mr-2" />
                        Claude AI Text Enhancement
                      </li>
                      <li className="flex items-center text-gray-300">
                        <CheckCircle2 className="h-4 w-4 text-green-400 mr-2" />
                        Theme App Extension
                      </li>
                    </ul>
                    <Link to="/">
                      <Button className="bg-indigo-600 hover:bg-indigo-700">
                        Learn More About Speakify
                      </Button>
                    </Link>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <p className="text-center text-gray-500 mt-8">
              More apps coming soon...
            </p>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-6">
            <div className="text-center p-6">
              <div className="bg-white/10 w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Code2 className="h-8 w-8 text-indigo-400" />
              </div>
              <h4 className="text-lg font-semibold text-white mb-2">Quality Code</h4>
              <p className="text-gray-400 text-sm">Built with best practices and modern technologies</p>
            </div>
            
            <div className="text-center p-6">
              <div className="bg-white/10 w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Rocket className="h-8 w-8 text-purple-400" />
              </div>
              <h4 className="text-lg font-semibold text-white mb-2">Innovation</h4>
              <p className="text-gray-400 text-sm">Pushing boundaries with cutting-edge AI</p>
            </div>
            
            <div className="text-center p-6">
              <div className="bg-white/10 w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Users className="h-8 w-8 text-green-400" />
              </div>
              <h4 className="text-lg font-semibold text-white mb-2">User-Centric</h4>
              <p className="text-gray-400 text-sm">Designed for real merchant needs</p>
            </div>
            
            <div className="text-center p-6">
              <div className="bg-white/10 w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Globe className="h-8 w-8 text-orange-400" />
              </div>
              <h4 className="text-lg font-semibold text-white mb-2">Global Reach</h4>
              <p className="text-gray-400 text-sm">Solutions for merchants worldwide</p>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-16 bg-black/20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h3 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Let&apos;s Build Something Amazing
          </h3>
          <p className="text-gray-300 text-lg mb-8">
            Have an idea for an AI-powered e-commerce application? We&apos;d love to hear from you.
          </p>
          <a href="mailto:question2abhi@gmail.com">
            <Button 
              size="lg"
              className="bg-gradient-to-r from-indigo-500 to-purple-500 hover:from-indigo-600 hover:to-purple-600 text-white font-semibold px-8 py-4 text-lg"
            >
              <Mail className="h-5 w-5 mr-2" />
              Get in Touch
            </Button>
          </a>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-white/10 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center space-x-3">
              <div className="bg-gradient-to-r from-indigo-500 to-purple-500 p-2 rounded-lg">
                <Building2 className="h-5 w-5 text-white" />
              </div>
              <span className="text-white font-semibold">Vortex AI Advisory</span>
            </div>
            <p className="text-gray-500 text-sm">
              © {new Date().getFullYear()} Vortex AI Advisory. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default VortexAIAdvisory;
